import { z } from "zod";
import { COLLECTION_STATUS, memberDueStatusSchema } from "./collections.js";
// 🔴 DÙNG LẠI enum chu kỳ của lịch thu — KHÔNG khai enum thứ hai ở đây (§3.8). Hai bản chép sẽ
// trôi lệch, và thứ trôi lệch là chu kỳ hiển thị cho người dùng.
import { collectionFrequencySchema, scheduleStatusSchema } from "./collection-schedules.js";
import { groupRoleSchema, groupCategoryEmbeddedSchema } from "./groups.js";

/* ===========================================================================
 * US-E24.F18-0367-S01 (@UC9623 @UC9624) — TIẾN ĐỘ ĐỢT THU THEO LÔ.
 * GET /v1/funds/:id/collection-progress — @gate money-display, rbac. **0 bút toán**.
 *
 * 🔴 VÌ SAO TỒN TẠI: màn C1 (Figma `365:21779`) vẽ trên MỖI card đợt thu *"Đã đóng 4/12 · Đã thu
 * 700.000đ"*. Nguồn duy nhất đang có là `GET /v1/collections/:id/status` — **một call cho MỘT đợt**
 * (`collection-period-status.controller.ts:30`) và bên trong là **một `groupBy` mỗi đợt**
 * (`collection-period-status.service.ts:53`). Quỹ 20 đợt ⇒ **20 round-trip** cho một màn.
 * Endpoint này **đóng gói lại theo LÔ**; nó **KHÔNG** tính lại tiền — hai công thức "đã thu" trong
 * cùng hệ thống là chỗ để card và màn chi tiết cãi nhau bằng hai con số.
 *
 * 🔴 REDACTION THEO VAI — GIỮ NGUYÊN LUẬT ĐANG CÓ, KHÔNG NỚI, KHÔNG SIẾT:
 *  - **Đếm người** (`totalMembers`/`paidMembers`/`progressPercent`): **mọi member ACTIVE** đọc được —
 *    mirror `CollectionAccessGuard` **bare** của `/collections/:id/status`.
 *  - **Tiền** (`collectedMinor`…): **chỉ OWNER/ADMIN/TREASURER** — mirror
 *    `@RequireFundRoles(OWNER, ADMIN, TREASURER)` ở `payment-board.controller.ts:36`.
 *    Đây là luật privacy **đã chốt viết thành chữ** (`collection-period-status.service.ts:14-15`:
 *    *"AGGREGATE-ONLY … response SHAPE KHÔNG có amountMinor/số tiền của due người khác"*), **không**
 *    phải thiếu sót. Nới nó cho MEMBER là **quyết định sản phẩm về minh bạch** ⇒ cần QĐ PO
 *    (index.md §7a), không phải việc của code.
 *
 * 🔴 DISCRIMINATED UNION theo `redacted`, KHÔNG field optional: với `collectedMinor?: string` thì
 * *"ẩn"*, *"quên điền"* và `collectedMinor: "0"` **đều hợp lệ về kiểu** — mà `0` là **nói dối về
 * tiền** (CLAUDE.md §3.8). Nhánh `redacted: true` `.strict()` **không có** field tiền nào ⇒ server lỡ
 * nhét số vào thì `parse` ném, **số bị ẩn KHÔNG rời API**.
 *
 * 🔴 KHÔNG có chip *"Đã hoàn thành"* ở đây. `Collection.status='closed'` hiện chỉ được set trong
 * seeder demo AI-preview (`seed-sandbox.builder.ts:415,423` — v1, đã xoá) — **không luồng nghiệp vụ thật nào**
 * đóng một đợt. Nguồn của sub-tab đó đến từ `US-E24.F2-0351-collection-adhoc-lifecycle`. Ở đây chỉ
 * **passthrough** `status` thô; **tuyệt đối không** tự chế định nghĩa "đợt đã xong" thứ hai (vd
 * *"đã thu đủ 100%"*) — hai định nghĩa cho cùng một trạng thái tiền là chỗ để sai tiền chui vào.
 * ========================================================================= */

/** Tiền trên wire = STRING minor units, KHÔNG âm (tổng của các khoản không âm). */
const MONEY_INTEGER_STRING_PATTERN = /^\d+$/;

/** Trạng thái đợt thu — dựng TỪ `COLLECTION_STATUS` để không đẻ whitelist thứ hai. */
export const collectionProgressStatusSchema = z.enum([
  COLLECTION_STATUS.OPEN,
  COLLECTION_STATUS.CLOSED,
]);

export type CollectionProgressStatus = z.infer<typeof collectionProgressStatusSchema>;

/**
 * Mặc định 50: màn C1 phải lấy được cả trang trong **MỘT** lời gọi (AC1 đo ở quỹ 20 đợt).
 * Trần 100: cap read defensive — mirror `MAX_PERIODS_PER_MEMBER = 500` /
 * `MAX_SCHEDULES_PER_FUND` (không `findMany` không biên).
 */
export const COLLECTION_PROGRESS_DEFAULT_LIMIT = 50;
export const COLLECTION_PROGRESS_MAX_LIMIT = 100;

/** Phần AI/ MỌI VAI đều thấy: đếm người + mốc thời gian + trạng thái của CHÍNH caller. */
const collectionProgressItemBase = {
  /**
   * 🔴 FUND-DEBT-REPAY-PATH (QĐ 06/10 §7(2) phương án a) — đợt HỆ THỐNG "nợ quỹ" (`kind = fund_debt`): VẪN trả ở đây để sheet "Nộp quỹ" ghép
   * được khoản (trả nợ bằng đường thu có sẵn), nhưng client PHẢI ẩn khỏi danh sách đợt + tiến độ. Thuộc tính của đợt, không phải tiền ⇒
   * CẢ HAI nhánh redaction.
   */
  hiddenFromList: z.boolean(),
  collectionId: z.string().uuid(),
  collectionName: z.string().nullable(),
  /**
   * US-E24.F38-0452 (@UC0039 @UC0050) — MÔ TẢ khoản thu ("tiền này dùng vào việc gì").
   *
   * 🔴 Đây là nửa ĐỌC của `US-E24.F36-0450`. Nửa đó chỉ làm đường GHI: mô tả xuống tới DB rồi
   * **không API nào trả về** ⇒ người dùng gõ xong không thấy lại ở bất kỳ đâu, tức ô "Mô tả*" vô
   * nghĩa. Một ô nhập chỉ có giá trị khi đọc lại được.
   *
   * `null` = đợt thu tạo trước khi có cột này, hoặc người tạo bỏ trống.
   */
  description: z.string().nullable(),
  /**
   * US-E24.F40-0454 (@UC0059) — MÃ KỲ: `'2026-09'` (tháng · năm học) · `'2026-Q3'` (quý) ·
   * `'2026'` (năm) · `'2026-W35'` (tuần ISO). `null` với đợt thu MỘT LẦN (không thuộc kỳ nào).
   *
   * 🔴 Client chọn nhãn theo `frequency` ngay dưới, **KHÔNG đoán bằng hình dạng chuỗi này**
   * (`có chữ Q thì là quý…`). Đoán theo hình dạng vỡ đúng ở NĂM HỌC: nó lưu **giống hệt** tháng.
   */
  periodKey: z.string().nullable(),
  /**
   * US-E24.F40-0454 (@UC0059) — khoảng NGÀY của kỳ (`YYYY-MM-DD`), CHỈ để hiển thị nhãn.
   *
   * Tháng/quý/năm đọc được từ `periodKey`. **TUẦN thì không**: `'2026-W35'` là "tuần thứ 35 của
   * năm", không phụ huynh nào đọc được — thẻ phải hiện *"Tuần 24–30/8"*, tức cần khoảng ngày.
   *
   * 🔴 LẤY TỪ CỘT ĐÃ LƯU lúc sinh kỳ, KHÔNG tính lại ở đường đọc: tính lại là dựng luật lịch thứ
   * hai, và repo đã có cảnh báo sẵn — *"đường hiển thị và đường sinh kỳ lệch nhau ⇒ người dùng thấy
   * một ngày, bị thu vào ngày khác."*
   *
   * `null` với đợt một lần và kỳ sinh trước bản này (client rơi về nhãn từ `periodKey`).
   */
  periodStart: z.string().nullable(),
  periodEnd: z.string().nullable(),
  /**
   * 🔴 US-E24.F61-0752 (@UC0059 · QĐ người dùng 09/09/2026) — ĐÂY LÀ KỲ THỨ MẤY CỦA LỊCH, đếm từ **1**.
   *
   * Thẻ kỳ ở màn "Chi tiết đợt thu" phải đọc là *"Tuần 1"*, *"Tuần 2"*… và *"Học kỳ 1"* — người dùng
   * đếm theo ĐỢT THU của họ, không theo lịch dương. Số này KHÁC hẳn số tuần ISO trong `periodKey`
   * (`'2026-W35'` = tuần thứ 35 **của năm**, không phụ huynh nào đọc được).
   *
   * ═══ VÌ SAO SERVER ĐẾM, KHÔNG PHẢI CLIENT ═══
   * Client có sẵn danh sách kỳ (nó gọi chính endpoint này với `?scheduleId=`) nên đếm ở đó trông
   * "hiển nhiên đúng" — và sai một cách im lặng: danh sách CÓ CURSOR, trang đầu là **50 kỳ MỚI
   * NHẤT** (`createdAt desc`). Lịch tuần chạy hơn một năm ⇒ client sẽ gắn *"Tuần 1"* cho kỳ cũ nhất
   * **của trang**, tức tuần thứ 53 của lịch. Đúng mẫu hình mà `scheduleId` ngay dưới sinh ra để
   * chặn: một phép lọc/đếm dựa trên "thứ màn hình đang thấy" thì **nói dối kể từ trang thứ hai**.
   *
   * `null` = đợt LẺ (không thuộc lịch nào) **hoặc** kỳ cũ chưa có `periodStart` để xếp thứ tự —
   * client rơi về nhãn khoảng ngày như trước, không bịa ra một số thứ tự.
   *
   * 🔴 `.positive()` chứ không `nonnegative()`: kỳ đầu là **1**. Một số `0` lọt ra sẽ hiện *"Tuần 0"* —
   * câu vô nghĩa mà không lỗi nào báo.
   *
   * Ở CẢ HAI nhánh redaction: "kỳ thứ mấy" là thuộc tính của ĐỢT, không phải một con số tiền.
   */
  periodIndex: z.number().int().positive().nullable(),
  /**
   * 🔴 US-E24.F35-0449 (@UC9623) — quỹ SỞ HỮU đợt này.
   *
   * Có ở CẢ HAI nhánh redaction: **tên quỹ không phải tiền**. Giấu số tiền của một nhóm không có
   * nghĩa là giấu chuyện nhóm đó có quỹ nào.
   *
   * Bắt buộc (không optional) kể cả ở envelope cấp QUỸ — nơi `fundId` trùng `fundId` của envelope.
   * Dư một chút ở đó, nhưng nó giữ **MỘT** định nghĩa item cho cả hai phạm vi; item optional-field
   * sẽ đẻ ra ca *"quỹ nào?"* không biểu diễn được ở danh sách cấp nhóm — đúng thứ AC2 sinh ra để chặn.
   */
  fundId: z.string().uuid(),
  fundName: z.string(),
  /** Passthrough `Collection.status` — KHÔNG suy diễn, KHÔNG định nghĩa "hoàn thành" thứ hai. */
  status: collectionProgressStatusSchema,
  /** Hạn đóng 'YYYY-MM-DD' (@db.Date), null nếu đợt không đặt hạn. */
  dueDate: z.string().nullable(),
  /**
   * Số member PHẢI ĐÓNG trong đợt (Y của "X/Y") = số due **KHÔNG** `waived`.
   * 🔴 US-E6.F8-0862 (QĐ người dùng 29/09/2026): người được miễn không kỳ vọng thu ⇒ không nằm trong
   * mẫu số — cùng cách hiểu với `expectedMinor`/`collectedPercent`. Trùng `/collections/:id/status`.
   */
  totalMembers: z.number().int().nonnegative(),
  /** Số member đã đóng ĐỦ — paid + paid_on_behalf (X của "X/Y"). */
  paidMembers: z.number().int().nonnegative(),
  /**
   * 🔴 US-E24.F23-0378-S02 (@UC9660, QĐ PO 09/08/2026) — số member ĐANG ĐÓNG DỞ (`partially_paid`).
   *
   * TÁCH BẠCH với `paidMembers` theo đúng quyết định: *"4/12 đã đóng đủ · 1 người đóng một phần"*.
   * Cộng họ vào `paidMembers` sẽ nói dối (họ chưa đóng xong); bỏ họ đi cũng nói dối (quỹ đang giữ
   * tiền của họ). Hai chỉ số, hai câu, client tự ghép câu chữ — BE không hard-code chuỗi UI (§7).
   */
  partiallyPaidMembers: z.number().int().nonnegative(),
  /**
   * paidMembers/totalMembers*100 (int). `totalMembers = 0` ⇒ 0 (KHÔNG chia 0) — TRỪ đợt mà mọi due
   * đều `waived` (`isFullyCollected = true`): 100, vì không còn ai phải đóng (US-E6.F8-0862 Q1).
   */
  progressPercent: z.number().int().min(0).max(100),
  /**
   * Ngày còn lại tới hạn (âm = quá hạn N ngày). null nếu không có hạn.
   *
   * 🔴 CỐ Ý vẫn là SỰ THẬT VỀ NGÀY thuần tuý, kể cả khi đợt đã thu đủ: nó trả lời *"hạn cách đây
   * bao lâu"*, không trả lời *"có đang trễ nải không"*. Vế thứ hai là [isOverdue] ngay dưới.
   */
  daysLeft: z.number().int().nullable(),
  /**
   * 🔴🔴 US-E24.F62-0753 (@UC9623 · bug người dùng 09/09/2026) — QUÁ HẠN = **ĐÃ TRỄ HẠN _VÀ_ CÒN THIẾU**.
   *
   * ═══ BUG ═══
   * Trước bản này cờ chỉ so NGÀY (`dueDate < now`), nên một đợt **mọi người đã đóng đủ** vẫn báo
   * *"Quá hạn"* mãi mãi. Đo trên dữ liệu thật: kỳ tuần 17–24/8, 2/2 đã đóng, 0 đồng còn thiếu ⇒
   * thẻ vẫn đỏ chữ "QUÁ HẠN", và nó nằm ngay cạnh một kỳ khác cũng đã đóng đủ nhưng đọc là
   * "Hoàn thành" — cùng một tình trạng tiền, hai câu trả lời khác nhau.
   *
   * Nó KHÔNG phải ca hiếm: worker sinh kỳ định kỳ và **không bao giờ tự đóng sổ** kỳ nào
   * (`status='closed'` chỉ đến từ `PATCH .../close` do người bấm). Nên MỌI kỳ cũ đã thu đủ đều rơi
   * vào đúng ca này.
   *
   * Cùng khiếm khuyết đã được ghi ở tầng AI (`ai-query.service.ts` v1 đã xoá, review #544): trợ lý phát biểu
   * *"12/12 người đã đóng, đã quá hạn 47 ngày"* — vô nghĩa. Tầng đó vá bằng câu chữ; đây vá ở NGUỒN.
   *
   * ⚠️ KHÔNG xét `status`: một đợt **đã đóng sổ mà vẫn còn người chưa đóng** thì `true` là đúng —
   * tiền đó thật sự vừa trễ vừa thiếu. Vòng đời là trục RIÊNG, consumer đọc `status` cho việc đó.
   */
  isOverdue: z.boolean(),
  /**
   * 🔴 US-E24.F62-0753 — "đợt này KHÔNG CÒN AI THIẾU". Nguồn của nhãn *"Hoàn thành"*.
   *
   * 🔴 ĐÚNG VỊ-TỪ mà query `?fullyPaid=true` lọc, không phải một định nghĩa thứ hai: có **ít nhất
   * một** due VÀ **không** due nào mang trạng thái trong `OUTSTANDING_DUE_STATUSES`. Hai vế đều
   * cần thiết — đợt chưa có due nào cũng thoả "không due nào còn thiếu", mà gọi một đợt chưa ai
   * phải đóng là "đã hoàn thành" thì tab đó sẽ đầy những đợt vừa tạo.
   *
   * VÌ SAO SERVER TRẢ, KHÔNG ĐỂ CLIENT SUY TỪ `paidMembers >= totalMembers`: hai công thức từng lệch
   * nhau ở **người được MIỄN** (`waived`) khi mẫu số còn đếm họ. Từ US-E6.F8-0862 (QĐ 29/09) mẫu số
   * đã loại `waived` ⇒ `isFullyCollected ∧ totalMembers > 0 ⇒ paidMembers = totalMembers`; nhưng hai
   * phép vẫn lệch ở đợt **toàn miễn** (0/0 — cờ này `true`, còn `0 >= 0` thì đúng cả với đợt chưa có
   * due nào). "Có ít nhất một due" đếm THÔ, gồm `waived`. Một câu hỏi, một nguồn.
   *
   * Ở CẢ HAI nhánh redaction: đây là kết luận về TRẠNG THÁI đóng góp, không phải một con số tiền —
   * và vai bị ẩn tiền vốn đã thấy `paidMembers`/`totalMembers`.
   */
  isFullyCollected: z.boolean(),
  /** Status due của CHÍNH caller trong đợt. null nếu caller không có due. */
  myStatus: memberDueStatusSchema.nullable(),
  /**
   * 🔴🔴 US-E24.F60-0751 — LỊCH SINH RA ĐỢT NÀY. `null` = **đợt lẻ** (thu một lần), KHÔNG phải
   * "chưa biết".
   *
   * ═══ VÌ SAO THÊM, VÀ VÌ SAO NÓ TỪNG LÀ MỘT LỖ IM LẶNG ═══
   * Màn "Chi tiết đợt thu" dựng khối **các kỳ trước** bằng cách gọi lại chính endpoint này với
   * `?scheduleId=` — nhưng nó lấy id đó TỪ item đang mở, mà item **chưa bao giờ mang** field này.
   * Hệ quả đo được trên dữ liệu thật (người dùng gửi 09/09/2026): mọi item về tới client đều
   * `scheduleId == null` ⇒ client kết luận "đợt lẻ" ⇒ **không bao giờ gọi lượt thứ hai** ⇒ khối
   * "kỳ trước" RỖNG với 100% đợt định kỳ.
   *
   * ⚠️ Lưới test KHÔNG bắt được vì nó tự dựng DTO có `scheduleId` — tức chứng minh hàm chạy đúng
   * với một đầu vào mà server không bao giờ gửi (`CLAUDE.md` §3.6: *"test-xanh chứng minh hàm
   * đúng, không chứng minh hàm được DÙNG đúng đầu vào"*).
   *
   * Ở phần **KHÔNG bị ẩn**, cùng lý lẽ với `frequency` ngay dưới: đợt này thuộc lịch nào là thuộc
   * tính của ĐỢT, không phải một con số tiền.
   */
  scheduleId: z.string().uuid().nullable(),
  /**
   * 🔴🔴 US-E24.F63-0754 (@UC0038 @UC9623 · QĐ người dùng 09/09/2026) — LỊCH NÀY CÒN CHẠY KHÔNG.
   *
   * `null` = đợt LẺ (không thuộc lịch nào). Ngược lại là trạng thái của `CollectionSchedule`:
   * `active` · `paused` (đã dừng, bật lại được) · `cancelled` (huỷ hẳn).
   *
   * ═══ VÌ SAO CẦN Ở ĐÂY ═══
   * Danh sách đợt thu trước bản này chỉ biết `Collection.status` (open/closed) — trạng thái của MỘT
   * KỲ. Nên nó không phân biệt được hai chuyện hoàn toàn khác nhau mà người dùng nhìn thấy cùng một
   * chỗ: *"kỳ này đã ngừng thu, tuần sau vẫn thu tiếp"* và *"cả quỹ đã dừng"*. Thiếu nó thì thẻ
   * không đặt được tên đúng cho chính mình, và màn cài đặt không biết nên hiện nút *kích hoạt kỳ*
   * hay *kích hoạt cả quỹ*.
   *
   * ⚠️ Cờ HIỂN THỊ, KHÔNG phải chốt chặn: server vẫn từ chối `reopen` một kỳ khi lịch đang dừng
   * (mã `SCHEDULE_PAUSED`). Client ẩn nút chỉ để người dùng không bấm vào một thứ chắc chắn 409.
   */
  scheduleStatus: scheduleStatusSchema.nullable(),
  /**
   * 🔴 US-E24.F34-0448 (@UC9623) — chu kỳ của LỊCH sinh ra đợt này
   * (`CollectionSchedule.frequency`, `schema.prisma:1524`). `null` khi đợt là **one-time**
   * (`Collection.scheduleId` null) — `null` ở đây nghĩa là *"đợt lẻ"*, không phải *"chưa biết"*.
   *
   * Ở phần **KHÔNG bị ẩn** (cả hai nhánh `redacted`): chu kỳ là thuộc tính của ĐỢT, không phải tiền.
   * Giấu số tiền của một nhóm không có nghĩa là giấu chuyện nhóm đó thu hàng tháng.
   *
   * 🔴 **BẪY: repo có HAI enum "chu kỳ" khác nhau — đừng nối nhầm.**
   * | Nguồn | Giá trị |
   * |---|---|
   * | `CollectionSchedule.frequency` (ĐÚNG cho field này) | `weekly · monthly · quarterly · yearly · school_year` |
   * | `Fund.settings.fund_type_config.cycle` (`funds.ts:51`) | `monthly · quarterly` |
   * Lấy `cycle` sẽ mất `school_year` — chu kỳ của quỹ phụ huynh, tức ca dùng CHÍNH của sản phẩm.
   *
   * ⚠️ `"2 tuần/lần"` = `{frequency: weekly, recurrence.interval: 2}` — repo **cố ý không có** enum
   * `biweekly` (hai cách biểu diễn cùng một thứ sẽ thành hai đường tính rồi lệch nhau, §3.8). Client
   * muốn hiện đúng "2 tuần/lần" phải đọc thêm `interval`; chỉ đọc field này sẽ hiện "hàng tuần".
   */
  frequency: collectionFrequencySchema.nullable(),
};

export const collectionProgressItemSchema = z.discriminatedUnion("redacted", [
  z
    .object({
      ...collectionProgressItemBase,
      redacted: z.literal(false),
      currency: z.string().length(3),
      /** Đã thu = Σ amountMinor của due paid + paid_on_behalf. */
      collectedMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
      /** Còn thiếu = Σ amountMinor của due pending + overdue + awaiting_confirmation + partially_paid. */
      outstandingMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
      /** Kỳ vọng thu = collected + outstanding (KHÔNG gồm waived — miễn thì không kỳ vọng thu). */
      expectedMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
      /**
       * 🔴 US-E24.F34-0448 (@UC9623) — suất/người của đợt (`Collection.amountPerMemberMinor`).
       *
       * **LÀ TIỀN** ⇒ chỉ tồn tại ở nhánh này. Nhánh `redacted: true` `.strict()` không có field
       * này, nên server lỡ nhét vào thì `parse` NÉM — số bị ẩn không rời API.
       *
       * `null` khi đợt **không có một mức chung**: `amountMode` ∈ {`per_member`, `tiered`,
       * `target_split`, `open_amount`} (`schema.prisma:1484`). Lúc đó `null` là câu trả lời ĐÚNG —
       * lấy đại số của người đầu tiên rồi hiện như "suất chung" là nói dối về tiền.
       */
      perMemberMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN).nullable(),
      /**
       * 🔴 US-E24.F47-0461 (@UC9623 @UC9659) — **tiến độ theo TIỀN**: `collected / expected × 100`,
       * số nguyên 0..100. `expected = 0` ⇒ `0` (không chia 0).
       *
       * KHÁC `progressPercent` ở phần chung ngay trên, và đó là toàn bộ lý do field này tồn tại:
       *   · `progressPercent`  = bao nhiêu **NGƯỜI** đã đóng ĐỦ
       *   · `collectedPercent` = bao nhiêu **TIỀN** đã về
       * Đợt 2 người × 10.000, quản trị nộp hộ 1.000 cho một người ⇒ `progressPercent: 0` nhưng
       * `collectedPercent: 5`. Bug người dùng báo 19/08/2026: thanh tiến độ nằm ngay dưới dòng
       * *"Đã thu: 1.000 / 20.000"* mà **rỗng trơn**, vì nó chỉ có con số đếm người để đọc.
       *
       * 🔴 Ở nhánh NÀY, không phải phần chung — nó là **thương của hai giá trị tiền**. Vai không được
       * xem `collectedMinor`/`expectedMinor` cũng không được biết quỹ đã thu tới đâu; đặt nó lên
       * phần chung là mở lại đúng cánh cửa mà `redacted` sinh ra để đóng. Client vai bị ẩn rơi về
       * thanh đếm NGƯỜI (`progressPercent`) — thứ họ vốn được thấy.
       *
       * 🔴 SERVER tính, không phải client. Đây là phép chia hai chuỗi minor units: client chia được
       * thì phải parse sang `double` (M1 cấm), và ở quỹ vượt 2^53 minor units phép chia đó sai LẶNG.
       */
      collectedPercent: z.number().int().min(0).max(100),
    })
    .strict(),
  z.object({ ...collectionProgressItemBase, redacted: z.literal(true) }).strict(),
]);

export type CollectionProgressItem = z.infer<typeof collectionProgressItemSchema>;

/**
 * Envelope `{ fundId, data, pageInfo }` — cursor keyset `(createdAt, id)` desc, opaque base64
 * (CLAUDE.md §7: cursor pagination bắt buộc cho MỌI list).
 *
 * 🔴 CỐ Ý KHÔNG có field tổng-toàn-quỹ ở đây. *"Số dư còn lại"* của Figma đã có nguồn
 * `GET /v1/funds/{id}/dashboard` — dựng lại ở đây là **công thức thứ hai cho cùng một con số**.
 */
export const fundCollectionProgressResponseSchema = z
  .object({
    fundId: z.string().uuid(),
    data: z.array(collectionProgressItemSchema),
    pageInfo: z
      .object({ hasNext: z.boolean(), nextCursor: z.string().nullable() })
      .strict(),
  })
  .strict();

export type FundCollectionProgressResponse = z.infer<
  typeof fundCollectionProgressResponseSchema
>;

/**
 * Envelope cấp NHÓM `{ groupId, data, pageInfo }` (US-E24.F35-0449 · @UC9623).
 *
 * 🔴 VÌ SAO KHÔNG ĐỂ CLIENT TỰ GỘP: bản cấp quỹ phân trang cursor keyset `(createdAt, id)` của
 * **TỪNG quỹ**. Nhóm N quỹ ⇒ client giữ N cursor rồi tự trộn — tức **client tự quyết thứ tự và ranh
 * giới trang của một danh sách TIỀN**, cộng N round-trip mỗi lần kéo-làm-mới.
 *
 * 🔴 DÙNG LẠI `collectionProgressItemSchema` NGUYÊN VẸN — chỉ đổi envelope. Định nghĩa item thứ hai
 * cho cùng một thứ là đúng thứ CLAUDE.md §3.8 cấm, và thứ trôi lệch sẽ là con số tiền hiển thị.
 *
 * 🔴 CỐ Ý KHÔNG có tổng-toàn-nhóm — cùng lý do với bản cấp quỹ, nhân lên N lần: cộng `expectedMinor`
 * của các đợt KHÔNG ra số dư quỹ (§3.7 — nghĩa vụ ≠ đã xác nhận), và số dư đã có nguồn
 * `GET /v1/funds/{id}/dashboard`.
 */
export const groupCollectionProgressResponseSchema = z
  .object({
    groupId: z.string().uuid(),
    /**
     * 🔴 US-E24.F59-0524 (@UC9565) — vai của CHÍNH người gọi trong nhóm này.
     *
     * VÌ SAO Ở ĐÂY: màn "Chi tiết đợt thu" của app được GHÉP từ endpoint này + `payment-board`
     * (không có endpoint detail gộp — xem `groups_repository_impl.getCollectionDetail`). Nó cần
     * biết người đang xem có được sửa đợt thu không, mà không field nào hiện có trả lời được:
     * `CollectionPeriodMember.role` là vai của TỪNG THÀNH VIÊN trong đợt, không phải vai người xem.
     *
     * Cùng khuôn `groups.ts:632` đã làm cho màn nhóm — *"role: vai trò của CHÍNH caller"*.
     *
     * ⚠️ Đây là dữ liệu để ẨN/HIỆN nút, KHÔNG phải chốt chặn. Chốt thật là
     * `@RequireFundRoles(OWNER, ADMIN)` ở endpoint sửa — client tin field này để không vẽ một nút
     * chắc chắn 403, chứ server không bao giờ tin client.
     */
    viewerRole: groupRoleSchema,
    /**
     * 🔴 US-E4.F1-0431 (@UC9677 · QĐ người dùng 08/09/2026) — từ vựng của loại nhóm.
     *
     * VÌ SAO Ở ĐÂY, cùng một lý do đã viết cho `viewerRole` ngay trên: màn "Chi tiết đợt thu" của
     * app được GHÉP từ endpoint này + `payment-board`, không có endpoint detail gộp. Route của màn
     * đó chỉ mang `groupId` + `collectionId`, và **notification deep-link thẳng vào nó**
     * (`NotificationTargetScreen.collectionDetail`) nên `extra` của go_router không dùng được —
     * mở từ tab thì "học sinh", mở từ thông báo thì "thành viên", CÙNG MỘT MÀN nói hai danh từ.
     *
     * Không thêm một request thứ hai chỉ để biết gọi suất là gì: cùng lý lẽ đã ghi ở
     * `groups_repository_impl.getMemberDetail` — *"từ vựng đi kèm CHÍNH response này"*.
     *
     * `null` = nhóm không có danh mục ⇒ client rơi về danh từ trung tính của ARB.
     */
    category: groupCategoryEmbeddedSchema.nullable(),
    data: z.array(collectionProgressItemSchema),
    pageInfo: z
      .object({ hasNext: z.boolean(), nextCursor: z.string().nullable() })
      .strict(),
  })
  .strict();

export type GroupCollectionProgressResponse = z.infer<
  typeof groupCollectionProgressResponseSchema
>;

/**
 * Query. `.strict()` ⇒ param lạ **400 tại biên, 0 row chạm DB**.
 *
 * 🔴 US-E24.F35-0449 (@UC9623) — `status` ĐÃ CÓ NGUỒN THẬT, comment cũ ở đây nay đã mục.
 * Bản trước ghi *"KHÔNG có filter `status`: sub-tab 'Đã hoàn thành' chưa có nguồn thật (⇐ F2-0351)"*.
 * **F2-0351 đã ship**: `PATCH /v1/funds/:id/collections/:collectionId/close` là producer THẬT của
 * `status='closed'` (docblock của chính nó tự nhận *"producer THẬT đầu tiên"*).
 *
 * Và câu *"client tự nhóm theo `data[].status`"* chỉ đúng khi client cầm **toàn bộ** danh sách. Với
 * cursor, lọc ở client chỉ lọc đúng **trang hiện tại** ⇒ tab "Đã hoàn thành" hiện rỗng trong khi
 * trang sau vẫn còn đợt đã đóng — **sai một cách im lặng**. Lọc phải chạy trong SQL.
 */
export const collectionProgressQuerySchema = z
  .object({
    cursor: z.string().optional(),
    limit: z.coerce
      .number()
      .int()
      .min(1)
      .max(COLLECTION_PROGRESS_MAX_LIMIT)
      .optional(),
    /** Lọc theo trạng thái đợt. Vắng ⇒ mọi trạng thái. Ngoài whitelist ⇒ 400 tại biên. */
    status: collectionProgressStatusSchema.optional(),
    /**
     * 🔴 US-E24.F59-0524 (@UC9567) — lọc theo ĐÃ THU ĐỦ CHƯA. Vắng ⇒ không lọc theo trục này.
     *
     * TRỤC ĐỘC LẬP với `status`, không phải giá trị thứ ba của nó. `status` là **vòng đời** do
     * người bấm (`open` ↔ `closed`); `fullyPaid` là **hệ quả của dữ liệu** (mọi người đã đóng
     * xong chưa). Nhét chung thành `status: 'completed'` là gộp hai khái niệm mà chính repo này
     * đã tách nhiều lần — và sẽ hỏng ngay khi cần hỏi "đợt đã đóng sổ mà chưa thu đủ".
     *
     * Ba tab của màn danh sách = ba lời gọi RIÊNG, mỗi lời gọi một bộ lọc (QĐ người dùng 24/08):
     *   Đang hoạt động  → `status=open&fullyPaid=false`
     *   Đã hoàn thành   → `status=open&fullyPaid=true`
     *   Ngừng hoạt động → `status=closed`
     *
     * 🔴 Lọc chạy TRONG SQL, không phải sau khi phân trang. `paidMembers` được tính ở bước 2 của
     * service (sau `findMany`), nên lọc dựa vào nó chỉ đúng TRANG HIỆN TẠI — đúng cái bẫy mà
     * docblock ngay trên đã cảnh báo cho việc lọc ở client: *"tab hiện rỗng trong khi trang sau
     * vẫn còn — sai một cách im lặng"*. Ngồi ở server không làm phép lọc sai đó thành đúng.
     */
    /**
     * 🔴 KHÔNG dùng `z.coerce.boolean()`: query string luôn là CHUỖI, và `Boolean("false")` là
     * **`true`** — nên `?fullyPaid=false` sẽ lọc ra đúng tập NGƯỢC LẠI, im lặng. Tab "Đang hoạt
     * động" sẽ hiện toàn đợt đã thu xong. int-spec `@UC9567` bắt được đúng ca này.
     */
    fullyPaid: z
      .enum(["true", "false"])
      .transform((v) => v === "true")
      .optional(),
    /**
     * US-E24.F38-0452 (@UC0059) — lọc theo LỊCH: chỉ các kỳ của một `CollectionSchedule`.
     *
     * 🔴 Màn "Chi tiết đợt thu" cần "các kỳ TRƯỚC của lịch này". Không có filter ở server thì client
     * phải tải hết rồi tự lọc — mà danh sách này CÓ cursor, nên mọi phép lọc dựa trên thứ màn hình
     * đang thấy sẽ **nói dối kể từ trang thứ hai**, một cách trông rất hợp lý.
     */
    scheduleId: z.string().uuid().optional(),
    /**
     * 🔴🔴 US-E24.F60-0751 — LẤY ĐÚNG MỘT ĐỢT theo id.
     *
     * Sinh ra cùng lúc với việc danh sách cấp NHÓM gộp kỳ (chỉ hiện kỳ hiện tại của mỗi lịch).
     * Màn "Chi tiết đợt thu" của app dựng dữ liệu bằng cách **tìm đợt trong TRANG ĐẦU** của danh
     * sách đó — một chỗ dựa vốn đã mong manh (đợt ở trang 2 là không tìm thấy), và sau khi gộp thì
     * nó SAI hẳn với mọi kỳ CŨ: thông báo `due.reminder` / `payment.confirmed` trỏ vào kỳ 3 trong
     * khi danh sách chỉ còn kỳ 5 ⇒ bấm vào tin ra màn "không tìm thấy đợt thu".
     *
     * Có filter này thì client hỏi thẳng đúng đợt nó cần, không phụ thuộc trang nào cả.
     *
     * 🔴 KHÔNG phải một endpoint đọc-một-đợt thứ hai: cùng service, cùng phép tính tiền, cùng luật
     * ẩn theo vai. Chỉ là một vế `WHERE id = ?` — nên không có công thức nào để trôi lệch.
     */
    collectionId: z.string().uuid().optional(),
  })
  .strict();

export type CollectionProgressQuery = z.infer<typeof collectionProgressQuerySchema>;
